import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useStore, { api } from '../store/useStore';
import DeviceCodeModal from '../components/DeviceCodeModal';

// ── Local storage layer ────────────────────────────────────────────────────────

const LS_DRAFTS = 'classmail_drafts_v1';
const LS_SENT   = 'classmail_sent_v1';
const ls = {
  getDrafts: () => { try { return JSON.parse(localStorage.getItem(LS_DRAFTS) || '[]'); } catch { return []; } },
  saveDraft: d => { const list = ls.getDrafts().filter(x => x.id !== d.id); list.unshift({ ...d, saved_at: new Date().toISOString() }); localStorage.setItem(LS_DRAFTS, JSON.stringify(list.slice(0, 100))); },
  delDraft:  id => { localStorage.setItem(LS_DRAFTS, JSON.stringify(ls.getDrafts().filter(x => x.id !== id))); },
  getSent:   () => { try { return JSON.parse(localStorage.getItem(LS_SENT) || '[]'); } catch { return []; } },
  addSent:   m  => { const list = ls.getSent(); list.unshift({ ...m, id: `sent_${Date.now()}`, sent_at: new Date().toISOString() }); localStorage.setItem(LS_SENT, JSON.stringify(list.slice(0, 200))); },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(str) {
  if (!str) return '';
  const d = new Date(str);
  const now = new Date();
  if (isNaN(d)) return str;
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const diff = (now - d) / 86400000;
  if (diff < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function fmtFull(str) {
  if (!str) return '';
  const d = new Date(str);
  if (isNaN(d)) return str;
  return d.toLocaleString([], { dateStyle: 'full', timeStyle: 'short' });
}

function fmtAgo(str) {
  if (!str) return '';
  const d = new Date(str);
  if (isNaN(d)) return '';
  const diff = Math.floor((Date.now() - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const FOLDER_ICONS = {
  inbox: '📥', sent: '📤', drafts: '✏️', spam: '🚫', trash: '🗑️',
  starred: '⭐', important: '🔔', archive: '📦', outbox: '📮',
};

function fileIcon(contentType) {
  if (!contentType) return '📎';
  if (contentType === 'application/pdf') return '📄';
  if (contentType.startsWith('image/')) return '🖼️';
  if (contentType.includes('wordprocessingml') || contentType.includes('msword')) return '📝';
  if (contentType.includes('spreadsheetml') || contentType.includes('ms-excel')) return '📊';
  if (contentType.includes('presentationml') || contentType.includes('ms-powerpoint')) return '📑';
  return '📎';
}

function fmtBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// ── Attachment components ─────────────────────────────────────────────────────

function AttachmentViewer({ url, contentType, name, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 2000, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#161b22', borderBottom: '1px solid #30363d' }}>
        <span style={{ color: '#e6edf3', fontSize: 14, fontWeight: 600 }}>{name}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href={url} download={name} className="btn btn-ghost btn-sm">↓ Download</a>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕ Close</button>
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {contentType.startsWith('image/') && (
          <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        )}
        {contentType === 'application/pdf' && (
          <iframe src={url} style={{ width: '100%', height: '100%', border: 'none' }} title={name} />
        )}
      </div>
    </div>
  );
}

function AttachmentBar({ accountId, messageId }) {
  const [attachments, setAttachments] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    if (!accountId || !messageId) return;
    api.get(`/api/emails/${accountId}/messages/${messageId}/attachments`)
      .then(r => setAttachments(r.data || []))
      .catch(() => setAttachments([]));
  }, [accountId, messageId]);

  if (!attachments.length) return null;

  const fetchBlob = async (att) => {
    setLoading(att.id);
    try {
      const resp = await api.get(
        `/api/emails/${accountId}/attachments/${encodeURIComponent(att.id)}?message_id=${encodeURIComponent(messageId)}`,
        { responseType: 'blob' }
      );
      return URL.createObjectURL(resp.data);
    } finally {
      setLoading(null);
    }
  };

  const handleClick = async (att) => {
    const ct = att.content_type || '';
    const blobUrl = await fetchBlob(att);
    if (ct.startsWith('image/') || ct === 'application/pdf') {
      setViewer({ url: blobUrl, contentType: ct, name: att.name });
    } else {
      const a = document.createElement('a'); a.href = blobUrl; a.download = att.name;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    }
  };

  return (
    <>
      <div style={{ padding: '10px 28px', borderTop: '1px solid #21262d', display: 'flex', flexWrap: 'wrap', gap: 8, background: '#0d1117' }}>
        <span style={{ fontSize: 11, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '.5px', alignSelf: 'center', marginRight: 4 }}>
          📎 {attachments.length} attachment{attachments.length !== 1 ? 's' : ''}
        </span>
        {attachments.map(att => (
          <button key={att.id} onClick={() => handleClick(att)} disabled={!!loading} title={`${att.name} · ${fmtBytes(att.size)}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#161b22', border: '1px solid #30363d', borderRadius: 20, padding: '4px 12px', cursor: loading ? 'wait' : 'pointer', color: '#c9d1d9', fontSize: 12, fontFamily: 'inherit', transition: 'background .1s', opacity: loading && loading !== att.id ? 0.5 : 1 }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#21262d'; }}
            onMouseLeave={e => e.currentTarget.style.background = '#161b22'}>
            <span>{loading === att.id ? '⏳' : fileIcon(att.content_type)}</span>
            <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name}</span>
            {att.size > 0 && <span style={{ color: '#6e7681', flexShrink: 0 }}>{fmtBytes(att.size)}</span>}
          </button>
        ))}
      </div>
      {viewer && <AttachmentViewer url={viewer.url} contentType={viewer.contentType} name={viewer.name} onClose={() => { URL.revokeObjectURL(viewer.url); setViewer(null); }} />}
    </>
  );
}

// ── Rich Text Editor ──────────────────────────────────────────────────────────

function RichEditor({ html, onChange }) {
  const ref = useRef(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (ref.current && !initialized.current) {
      ref.current.innerHTML = html || '';
      initialized.current = true;
    }
  }, []);

  // Reset when compose window is re-opened with new content (e.g. reply)
  useEffect(() => {
    if (ref.current && html && !ref.current.innerHTML) {
      ref.current.innerHTML = html;
    }
  }, [html]);

  const exec = (cmd, val = undefined) => {
    ref.current?.focus();
    document.execCommand(cmd, false, val ?? null);
    onChange(ref.current?.innerHTML || '');
  };

  const insertHTML = (htmlStr) => {
    ref.current?.focus();
    document.execCommand('insertHTML', false, htmlStr);
    onChange(ref.current?.innerHTML || '');
  };

  const insertImage = () => {
    const url = window.prompt('Image URL:', 'https://');
    if (url) insertHTML(`<img src="${url}" style="max-width:100%;height:auto;" alt=""/>`);
  };

  const insertLink = () => {
    const url = window.prompt('URL:', 'https://');
    if (url) exec('createLink', url);
  };

  const insertTable = () => {
    let rows = parseInt(window.prompt('Rows:', '3') || '3');
    let cols = parseInt(window.prompt('Columns:', '3') || '3');
    if (isNaN(rows) || rows < 1) rows = 3;
    if (isNaN(cols) || cols < 1) cols = 3;
    const cellStyle = 'border:1px solid #ccc;padding:6px 10px;min-width:60px;';
    let tbl = `<table style="border-collapse:collapse;width:100%;margin:8px 0;"><tbody>`;
    for (let r = 0; r < rows; r++) {
      tbl += '<tr>';
      for (let c = 0; c < cols; c++) {
        tbl += `<td style="${cellStyle}">&nbsp;</td>`;
      }
      tbl += '</tr>';
    }
    tbl += '</tbody></table><p></p>';
    insertHTML(tbl);
  };

  const Btn = ({ title, onExec, children, color }) => (
    <button title={title} onMouseDown={e => { e.preventDefault(); onExec(); }}
      style={{ background: 'none', border: '1px solid transparent', borderRadius: 4, color: color || '#c9d1d9', cursor: 'pointer', padding: '3px 7px', fontSize: 12, fontFamily: 'inherit', lineHeight: 1.4, transition: 'background .1s, border-color .1s', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 26 }}
      onMouseEnter={e => { e.currentTarget.style.background = '#2d333b'; e.currentTarget.style.borderColor = '#444c56'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}>
      {children}
    </button>
  );

  const Div = ({ k }) => <div key={k} style={{ width: 1, height: 16, background: '#444c56', margin: '0 3px', alignSelf: 'center', flexShrink: 0 }} />;

  const selStyle = { background: '#161b22', border: '1px solid #30363d', borderRadius: 4, color: '#c9d1d9', fontSize: 11, padding: '3px 4px', cursor: 'pointer' };

  return (
    <div style={{ border: '1px solid #30363d', borderRadius: 6, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={{ background: '#1c2128', borderBottom: '1px solid #30363d', userSelect: 'none' }}>

        {/* Row 1 — Undo/Redo | Font/Size | Basic formatting | Colors */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, padding: '4px 8px', borderBottom: '1px solid #2d333b' }}>
          <Btn title="Undo (Ctrl+Z)" onExec={() => exec('undo')}>↩</Btn>
          <Btn title="Redo (Ctrl+Y)" onExec={() => exec('redo')}>↪</Btn>
          <Div k="d0" />

          <select defaultValue="Segoe UI" onChange={e => exec('fontName', e.target.value)} onMouseDown={e => e.stopPropagation()} title="Font" style={{ ...selStyle, maxWidth: 120 }}>
            <option value="Segoe UI">Segoe UI</option>
            <option value="Arial">Arial</option>
            <option value="Georgia">Georgia</option>
            <option value="Verdana">Verdana</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Courier New">Courier New</option>
            <option value="Trebuchet MS">Trebuchet MS</option>
            <option value="Tahoma">Tahoma</option>
          </select>

          <select defaultValue="3" onChange={e => exec('fontSize', e.target.value)} onMouseDown={e => e.stopPropagation()} title="Size" style={{ ...selStyle, width: 52 }}>
            <option value="1">10px</option>
            <option value="2">13px</option>
            <option value="3">16px</option>
            <option value="4">18px</option>
            <option value="5">24px</option>
            <option value="6">32px</option>
            <option value="7">48px</option>
          </select>
          <Div k="d1" />

          <Btn title="Bold" onExec={() => exec('bold')}><strong>B</strong></Btn>
          <Btn title="Italic" onExec={() => exec('italic')}><em>I</em></Btn>
          <Btn title="Underline" onExec={() => exec('underline')}><u>U</u></Btn>
          <Btn title="Strikethrough" onExec={() => exec('strikeThrough')}><s>S</s></Btn>
          <Btn title="Subscript" onExec={() => exec('subscript')}>x₂</Btn>
          <Btn title="Superscript" onExec={() => exec('superscript')}>x²</Btn>
          <Div k="d2" />

          {/* Text color */}
          <label title="Text Color" style={{ display: 'inline-flex', alignItems: 'center', gap: 2, cursor: 'pointer', padding: '2px 5px', borderRadius: 4, border: '1px solid transparent', color: '#c9d1d9', fontSize: 12 }}
            onMouseEnter={e => { e.currentTarget.style.background = '#2d333b'; e.currentTarget.style.borderColor = '#444c56'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}>
            <span style={{ fontWeight: 700 }}>A</span>
            <input type="color" defaultValue="#000000" title="Text Color"
              onChange={e => exec('foreColor', e.target.value)} onMouseDown={e => e.stopPropagation()}
              style={{ width: 14, height: 14, border: 'none', background: 'none', cursor: 'pointer', padding: 0, borderRadius: 2 }} />
          </label>

          {/* Highlight */}
          <label title="Highlight" style={{ display: 'inline-flex', alignItems: 'center', gap: 2, cursor: 'pointer', padding: '2px 5px', borderRadius: 4, border: '1px solid transparent', color: '#c9d1d9', fontSize: 12 }}
            onMouseEnter={e => { e.currentTarget.style.background = '#2d333b'; e.currentTarget.style.borderColor = '#444c56'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}>
            <span style={{ background: '#ffff00', color: '#000', padding: '0 2px', borderRadius: 1, fontWeight: 700 }}>H</span>
            <input type="color" defaultValue="#ffff00" title="Highlight"
              onChange={e => exec('hiliteColor', e.target.value)} onMouseDown={e => e.stopPropagation()}
              style={{ width: 14, height: 14, border: 'none', background: 'none', cursor: 'pointer', padding: 0, borderRadius: 2 }} />
          </label>
        </div>

        {/* Row 2 — Format | Align | Lists | Indent | Link | Image | Table | HR | Code | Clear */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, padding: '4px 8px' }}>
          <select defaultValue="p" onChange={e => exec('formatBlock', e.target.value)} onMouseDown={e => e.stopPropagation()} title="Paragraph style" style={{ ...selStyle, width: 92 }}>
            <option value="p">Normal</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
            <option value="blockquote">Quote</option>
            <option value="pre">Code Block</option>
          </select>
          <Div k="d3" />

          <Btn title="Align Left" onExec={() => exec('justifyLeft')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="2" width="14" height="1.5"/><rect x="0" y="6" width="10" height="1.5"/><rect x="0" y="10" width="14" height="1.5"/></svg>
          </Btn>
          <Btn title="Align Center" onExec={() => exec('justifyCenter')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="2" width="14" height="1.5"/><rect x="2" y="6" width="10" height="1.5"/><rect x="0" y="10" width="14" height="1.5"/></svg>
          </Btn>
          <Btn title="Align Right" onExec={() => exec('justifyRight')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="2" width="14" height="1.5"/><rect x="4" y="6" width="10" height="1.5"/><rect x="0" y="10" width="14" height="1.5"/></svg>
          </Btn>
          <Btn title="Justify" onExec={() => exec('justifyFull')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="2" width="14" height="1.5"/><rect x="0" y="6" width="14" height="1.5"/><rect x="0" y="10" width="14" height="1.5"/></svg>
          </Btn>
          <Div k="d4" />

          <Btn title="Bullet List" onExec={() => exec('insertUnorderedList')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><circle cx="2" cy="4" r="1.3"/><rect x="5" y="3" width="9" height="1.5"/><circle cx="2" cy="8" r="1.3"/><rect x="5" y="7" width="9" height="1.5"/><circle cx="2" cy="12" r="1.3"/><rect x="5" y="11" width="9" height="1.5"/></svg>
          </Btn>
          <Btn title="Numbered List" onExec={() => exec('insertOrderedList')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><text x="0" y="5" fontSize="5" fontFamily="monospace">1.</text><rect x="5" y="3" width="9" height="1.5"/><text x="0" y="9" fontSize="5" fontFamily="monospace">2.</text><rect x="5" y="7" width="9" height="1.5"/><text x="0" y="13" fontSize="5" fontFamily="monospace">3.</text><rect x="5" y="11" width="9" height="1.5"/></svg>
          </Btn>
          <Btn title="Indent" onExec={() => exec('indent')}>→|</Btn>
          <Btn title="Outdent" onExec={() => exec('outdent')}>|←</Btn>
          <Div k="d5" />

          <Btn title="Insert Link" onExec={insertLink} color="#58a6ff">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 9l4-4M3.5 7.5 2 9a2.83 2.83 0 0 0 4 4l1.5-1.5M10.5 6.5 12 5a2.83 2.83 0 0 0-4-4L6.5 2.5" strokeLinecap="round"/></svg>
          </Btn>
          <Btn title="Insert Image" onExec={insertImage}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="1" y="2" width="12" height="10" rx="1.5"/><circle cx="4.5" cy="5.5" r="1.2"/><path d="M1 10l3.5-3.5 2.5 2.5 2-2L13 10"/></svg>
          </Btn>
          <Btn title="Insert Table" onExec={insertTable}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="1" y="1" width="12" height="12" rx="1"/><line x1="5" y1="1" x2="5" y2="13"/><line x1="9" y1="1" x2="9" y2="13"/><line x1="1" y1="5" x2="13" y2="5"/><line x1="1" y1="9" x2="13" y2="9"/></svg>
          </Btn>
          <Btn title="Horizontal Rule" onExec={() => insertHTML('<hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>')}>—</Btn>
          <Div k="d6" />
          <Btn title="Remove Formatting" onExec={() => exec('removeFormat')} color="#8b949e">✕</Btn>
        </div>
      </div>

      {/* Editable area */}
      <div ref={ref} contentEditable suppressContentEditableWarning
        onInput={e => onChange(e.currentTarget.innerHTML)}
        style={{ flex: 1, minHeight: 220, overflowY: 'auto', padding: '14px 16px', color: '#000', background: '#fff', fontSize: 14, fontFamily: "'Segoe UI', -apple-system, sans-serif", lineHeight: 1.6, outline: 'none' }}
      />
    </div>
  );
}

// ── List parsing & tag utils (compose) ───────────────────────────────────────

function parseContactList(text, filename) {
  const ext = (filename.split('.').pop() || '').toLowerCase();
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (ext === 'csv' && lines.length > 1) {
    const parseRow = line => {
      const result = []; let cur = ''; let q = false;
      for (const ch of line) {
        if (ch === '"') q = !q;
        else if (ch === ',' && !q) { result.push(cur.trim()); cur = ''; }
        else cur += ch;
      }
      result.push(cur.trim());
      return result;
    };
    const headers = parseRow(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''));
    const items = lines.slice(1).map(line => {
      const vals = parseRow(line);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
      // ensure .email key exists (try first column if no 'email' header)
      if (!obj.email && headers[0]) obj.email = obj[headers[0]];
      return obj;
    }).filter(r => r.email && r.email.includes('@'));
    return { items, columns: headers };
  } else {
    const items = lines.map(line => {
      const parts = line.split(',').map(s => s.trim());
      const obj = { email: parts[0] };
      if (parts[1]) obj.name = parts[1];
      parts.slice(2).forEach((v, i) => { obj[`field${i + 1}`] = v; });
      return obj;
    }).filter(r => r.email && r.email.includes('@'));
    const columns = items.length > 0 ? Object.keys(items[0]) : ['email'];
    return { items, columns };
  }
}

function applyTagsCompose(template, row, senderName, senderEmail) {
  let r = template || '';
  const now = new Date();
  const entries = { ...row, date: now.toLocaleDateString(), time: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), sender_name: senderName || '', sender_email: senderEmail || '' };
  for (const [k, v] of Object.entries(entries)) {
    const val = String(v || '');
    r = r.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'gi'), val)
         .replace(new RegExp(`\\[${k}\\]`, 'gi'), val)
         .replace(new RegExp(`%${k}%`, 'gi'), val);
  }
  return r;
}

// ── Compose Window ────────────────────────────────────────────────────────────

function ComposeWindow({ accounts, currentAccountId, initialData, draftId: draftIdProp, onClose, onSent }) {
  const draftIdRef = useRef(draftIdProp || 'draft_' + Date.now());
  const draftId = draftIdRef.current;
  const defaultAccountId = initialData?.from_account_id || currentAccountId || accounts[0]?.id;
  const defaultAccount = accounts.find(a => a.id === defaultAccountId);
  const [form, setForm] = useState({
    from_account_id: defaultAccountId,
    send_as_name: defaultAccount?.display_name || '',
    to: '', cc: '', bcc: '', reply_to: '', subject: '', body: '',
    quoted_html: '',
    ...initialData,
  });
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const [showBcc, setShowBcc] = useState(!!(initialData?.bcc));
  const [showReplyTo, setShowReplyTo] = useState(!!(initialData?.reply_to));
  const [minimized, setMinimized] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  // 'wysiwyg' | 'html' | 'plain'
  const [editorMode, setEditorMode] = useState('wysiwyg');

  // HTML upload
  const [rawHtml, setRawHtml] = useState(null);        // { html } locked raw template
  const [htmlPending, setHtmlPending] = useState(null); // { html } awaiting mode choice
  // Send-to-list
  const [listItems, setListItems] = useState(null);
  const [listCols, setListCols] = useState([]);
  const [sendInterval, setSendInterval] = useState(60);
  const [showListPanel, setShowListPanel] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [tagFormat, setTagFormat] = useState('curly');
  const [tagToast, setTagToast] = useState('');
  const htmlInputRef = useRef(null);
  const listInputRef = useRef(null);
  const tagToastTimer = useRef(null);
  const { startCampaign } = useStore();

  const isReply = !!(initialData?.message_id);
  const composeTitle = isReply ? (initialData?.reply_all ? 'Reply All' : 'Reply') : initialData?.is_forward ? 'Forward' : 'New Message';

  const switchMode = (newMode) => {
    if (newMode === editorMode) return;
    // Convert body when switching modes
    if (editorMode === 'plain' && newMode !== 'plain') {
      // plain → html/wysiwyg: wrap in <pre> preserving whitespace
      setForm(f => ({ ...f, body: `<pre style="font-family:inherit;white-space:pre-wrap">${f.body.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>` }));
    } else if (newMode === 'plain' && editorMode !== 'plain') {
      // html/wysiwyg → plain: strip tags
      setForm(f => ({ ...f, body: f.body.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<[^>]+>/g, '').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').replace(/&nbsp;/g, ' ').trim() }));
    }
    setEditorMode(newMode);
  };

  useEffect(() => {
    const t = setInterval(() => { ls.saveDraft({ id: draftId, ...form }); setLastSaved(new Date().toISOString()); }, 30000);
    return () => clearInterval(t);
  }, [draftId, form]);

  const handleHtmlFile = e => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setHtmlPending({ html: ev.target.result });
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleListFile = e => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const { items, columns } = parseContactList(ev.target.result, file.name);
      setListItems(items); setListCols(columns); setShowListPanel(true);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const copyTag = col => {
    const tag = tagFormat === 'curly' ? `{{${col}}}` : tagFormat === 'bracket' ? `[${col}]` : `%${col}%`;
    navigator.clipboard.writeText(tag).catch(() => {});
    setTagToast(`Copied ${tag}`);
    clearTimeout(tagToastTimer.current);
    tagToastTimer.current = setTimeout(() => setTagToast(''), 1800);
  };

  const startListCampaign = () => {
    if (!listItems?.length) { setErr('No contacts loaded'); return; }
    if (!form.subject) { setErr('Subject is required'); return; }
    const fromAccount = accounts.find(a => a.id === form.from_account_id);
    const bodyTemplate = rawHtml ? rawHtml.html
      : editorMode === 'plain'
        ? `<pre style="font-family:inherit;white-space:pre-wrap;margin:0">${form.body.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</pre>`
        : form.body;
    startCampaign({
      items: listItems,
      total: listItems.length,
      interval: Math.max(20, sendInterval),
      accountId: form.from_account_id,
      accountEmail: fromAccount?.email || fromAccount?.email_address || '',
      sendAsName: form.send_as_name || '',
      senderEmail: fromAccount?.email || fromAccount?.email_address || '',
      subject: form.subject,
      bodyTemplate,
    });
    onClose();
  };

  const send = async () => {
    setSending(true); setErr('');
    try {
      const rawBody = rawHtml ? rawHtml.html
        : editorMode === 'plain'
          ? `<pre style="font-family:inherit;white-space:pre-wrap;margin:0">${form.body.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</pre>`
          : form.body;
      const quotedBlock = form.quoted_html
        ? `<br><div style="border-left:3px solid #ccc;padding-left:12px;margin:8px 0;color:#555;font-size:13px">${form.quoted_html}</div>`
        : '';
      const bodyToSend = rawBody + quotedBlock;
      const payload = { to: form.to, cc: form.cc || '', bcc: form.bcc || '', reply_to: form.reply_to || '', subject: form.subject, body: bodyToSend, send_as_name: form.send_as_name };
      if (isReply) {
        await api.post(`/api/emails/${form.from_account_id}/reply`, { ...payload, message_id: initialData.message_id, reply_all: initialData.reply_all || false, thread_id: initialData.thread_id });
      } else {
        await api.post(`/api/emails/${form.from_account_id}/send`, payload);
      }
      ls.delDraft(draftId);
      const fromAccount = accounts.find(a => a.id === form.from_account_id);
      ls.addSent({ ...form, from_email: fromAccount?.email, sent_at: new Date().toISOString() });
      onSent?.();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Send failed');
    }
    setSending(false);
  };

  const handleClose = () => {
    if (form.to || form.subject || form.body) ls.saveDraft({ id: draftId, ...form });
    onClose();
  };

  const fld = { width: '100%', background: 'transparent', border: 'none', padding: '7px 12px', color: '#e6edf3', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };

  const winStyle = expanded
    ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }
    : minimized
      ? { position: 'fixed', bottom: 0, right: 24, width: 660, zIndex: 1000 }
      : { position: 'fixed', bottom: 0, right: 24, width: 660, height: 580, zIndex: 1000 };

  return (
    <div style={{ ...winStyle, background: '#161b22', border: '1px solid #30363d', borderBottom: 'none', borderRadius: '8px 8px 0 0', boxShadow: '0 -4px 32px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <input ref={htmlInputRef} type="file" accept=".html,.htm" style={{ display: 'none' }} onChange={handleHtmlFile} />
      <input ref={listInputRef} type="file" accept=".txt,.csv" style={{ display: 'none' }} onChange={handleListFile} />
      {/* HTML upload mode-choice overlay */}
      {htmlPending && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 10, padding: 24, width: 320, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,.5)' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#e6edf3', marginBottom: 6 }}>How do you want to use this HTML?</div>
            <div style={{ fontSize: 12, color: '#8b949e', marginBottom: 20 }}>Tags (&#123;&#123;name&#125;&#125;, [name], %name%) work in both modes.</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => { setForm(f => ({ ...f, body: htmlPending.html })); setEditorMode('wysiwyg'); setRawHtml(null); setHtmlPending(null); }}
                style={{ background: '#238636', border: 'none', borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 600, padding: '10px 16px', cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1.4 }}>
                ✏️ Load into editor<br/><span style={{ fontSize: 10, fontWeight: 400, opacity: .8 }}>You can edit it</span>
              </button>
              <button onClick={() => { setRawHtml({ html: htmlPending.html }); setHtmlPending(null); }}
                style={{ background: '#1f6feb', border: 'none', borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 600, padding: '10px 16px', cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1.4 }}>
                🔒 Use as-is<br/><span style={{ fontSize: 10, fontWeight: 400, opacity: .8 }}>Locked — tags only</span>
              </button>
            </div>
            <button onClick={() => setHtmlPending(null)} style={{ marginTop: 12, background: 'none', border: 'none', color: '#6e7681', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' }}>Cancel</button>
          </div>
        </div>
      )}
      {/* Header */}
      <div style={{ height: 46, minHeight: 46, background: '#1c2128', padding: '0 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: minimized ? 'pointer' : 'default', flexShrink: 0, userSelect: 'none' }}
        onClick={minimized ? () => setMinimized(false) : undefined}>
        <span style={{ fontWeight: 600, color: '#e6edf3', fontSize: 13 }}>{composeTitle}</span>
        {/* Mode switcher / raw indicator */}
        {!minimized && (
          rawHtml ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(210,153,34,.15)', border: '1px solid rgba(210,153,34,.4)', borderRadius: 6, padding: '3px 10px' }} onClick={e => e.stopPropagation()}>
              <span style={{ fontSize: 11, color: '#d29922', fontWeight: 600 }}>🔒 Raw HTML</span>
              <button onClick={() => setRawHtml(null)} style={{ background: 'none', border: 'none', color: '#8b949e', cursor: 'pointer', fontSize: 11, padding: 0, fontFamily: 'inherit' }}>Remove</button>
            </div>
          ) : (
            <div style={{ display: 'flex', background: '#0d1117', borderRadius: 6, padding: 2, gap: 1 }} onClick={e => e.stopPropagation()}>
              {[['wysiwyg', 'Rich'], ['html', 'HTML'], ['plain', 'Plain']].map(([mode, label]) => (
                <button key={mode} onClick={() => switchMode(mode)}
                  style={{ padding: '3px 9px', fontSize: 11, fontFamily: 'inherit', cursor: 'pointer', borderRadius: 4, border: 'none', fontWeight: editorMode === mode ? 700 : 400, background: editorMode === mode ? '#30363d' : 'transparent', color: editorMode === mode ? '#e6edf3' : '#8b949e', transition: 'background .1s, color .1s' }}>
                  {label}
                </button>
              ))}
            </div>
          )
        )}
        <div style={{ display: 'flex', gap: 2 }}>
          <button title={minimized ? 'Restore' : 'Minimize'} onClick={e => { e.stopPropagation(); setMinimized(v => !v); setExpanded(false); }}
            style={{ background: 'none', border: 'none', color: '#8b949e', cursor: 'pointer', fontSize: 16, padding: '4px 7px', borderRadius: 4 }}
            onMouseEnter={e => e.currentTarget.style.color = '#e6edf3'} onMouseLeave={e => e.currentTarget.style.color = '#8b949e'}>—</button>
          <button title={expanded ? 'Restore' : 'Expand'} onClick={e => { e.stopPropagation(); setExpanded(v => !v); setMinimized(false); }}
            style={{ background: 'none', border: 'none', color: '#8b949e', cursor: 'pointer', fontSize: 13, padding: '4px 7px', borderRadius: 4 }}
            onMouseEnter={e => e.currentTarget.style.color = '#e6edf3'} onMouseLeave={e => e.currentTarget.style.color = '#8b949e'}>⤢</button>
          <button title="Close" onClick={e => { e.stopPropagation(); handleClose(); }}
            style={{ background: 'none', border: 'none', color: '#8b949e', cursor: 'pointer', fontSize: 16, padding: '4px 7px', borderRadius: 4 }}
            onMouseEnter={e => e.currentTarget.style.color = '#e6edf3'} onMouseLeave={e => e.currentTarget.style.color = '#8b949e'}>✕</button>
        </div>
      </div>

      {!minimized && (
        <>
          <div style={{ flexShrink: 0 }}>
            {err && <div style={{ background: 'rgba(248,81,73,.1)', border: '1px solid #f85149', margin: '8px 12px', borderRadius: 6, padding: '7px 10px', color: '#f85149', fontSize: 12 }}>{err}</div>}

            {/* From */}
            <div style={{ borderBottom: '1px solid #21262d', display: 'flex', alignItems: 'center' }}>
              <span style={{ padding: '0 12px', fontSize: 12, color: '#8b949e', width: 58, flexShrink: 0 }}>From</span>
              <select value={form.from_account_id || ''} onChange={e => {
                const id = parseInt(e.target.value);
                const acct = accounts.find(a => a.id === id);
                setForm(f => ({ ...f, from_account_id: id, send_as_name: acct?.display_name || '' }));
              }}
                style={{ flex: 1, background: 'transparent', border: 'none', color: '#e6edf3', fontSize: 13, fontFamily: 'inherit', outline: 'none', padding: '7px 0', cursor: 'pointer' }}>
                {accounts.map(a => <option key={a.id} value={a.id} style={{ background: '#161b22' }}>{a.email}</option>)}
              </select>
              <input value={form.send_as_name || ''} onChange={e => setForm(f => ({ ...f, send_as_name: e.target.value }))}
                placeholder="Display name" style={{ ...fld, flex: '0 0 150px', borderLeft: '1px solid #21262d', fontSize: 12, color: '#8b949e' }} />
            </div>

            {/* To */}
            <div style={{ borderBottom: '1px solid #21262d', display: 'flex', alignItems: 'center' }}>
              <span style={{ padding: '0 12px', fontSize: 12, color: '#8b949e', width: 58, flexShrink: 0 }}>To</span>
              <input value={form.to || ''} onChange={e => setForm(f => ({ ...f, to: e.target.value }))} placeholder="Recipients" style={{ ...fld, flex: 1 }} />
            </div>

            {/* CC */}
            <div style={{ borderBottom: '1px solid #21262d', display: 'flex', alignItems: 'center' }}>
              <span style={{ padding: '0 12px', fontSize: 12, color: '#8b949e', width: 58, flexShrink: 0 }}>CC</span>
              <input value={form.cc || ''} onChange={e => setForm(f => ({ ...f, cc: e.target.value }))} placeholder="CC" style={{ ...fld, flex: 1 }} />
              <div style={{ display: 'flex', gap: 0, flexShrink: 0 }}>
                {!showBcc && <button onClick={() => setShowBcc(true)} style={{ background: 'none', border: 'none', color: '#58a6ff', cursor: 'pointer', fontSize: 12, padding: '0 8px', flexShrink: 0 }}>BCC</button>}
                {!showReplyTo && <button onClick={() => setShowReplyTo(true)} style={{ background: 'none', border: 'none', color: '#58a6ff', cursor: 'pointer', fontSize: 12, padding: '0 8px', flexShrink: 0 }}>Reply-To</button>}
              </div>
            </div>

            {/* BCC */}
            {showBcc && (
              <div style={{ borderBottom: '1px solid #21262d', display: 'flex', alignItems: 'center' }}>
                <span style={{ padding: '0 12px', fontSize: 12, color: '#8b949e', width: 58, flexShrink: 0 }}>BCC</span>
                <input value={form.bcc || ''} onChange={e => setForm(f => ({ ...f, bcc: e.target.value }))} placeholder="BCC" style={{ ...fld, flex: 1 }} />
              </div>
            )}

            {/* Reply-To */}
            {showReplyTo && (
              <div style={{ borderBottom: '1px solid #21262d', display: 'flex', alignItems: 'center' }}>
                <span style={{ padding: '0 12px', fontSize: 12, color: '#8b949e', width: 58, flexShrink: 0 }}>Reply-To</span>
                <input value={form.reply_to || ''} onChange={e => setForm(f => ({ ...f, reply_to: e.target.value }))} placeholder="Replies will go here" style={{ ...fld, flex: 1 }} />
              </div>
            )}

            {/* Subject */}
            <div style={{ borderBottom: '1px solid #21262d', display: 'flex', alignItems: 'center' }}>
              <span style={{ padding: '0 12px', fontSize: 12, color: '#8b949e', width: 58, flexShrink: 0 }}>Subject</span>
              <input value={form.subject || ''} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Subject" style={{ ...fld, flex: 1 }} />
            </div>
          </div>

          {/* Tag bar — visible when list is loaded */}
          {listItems && (
            <div style={{ padding: '5px 12px', background: '#0d1117', borderBottom: '1px solid #21262d', display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 10, color: '#6e7681', flexShrink: 0, marginRight: 2 }}>Tags:</span>
              {/* Format selector */}
              <div style={{ display: 'flex', background: '#161b22', border: '1px solid #30363d', borderRadius: 4, overflow: 'hidden', marginRight: 4 }}>
                {[['curly', '{{}}'], ['bracket', '[]'], ['pct', '%%']].map(([f, lbl]) => (
                  <button key={f} onClick={() => setTagFormat(f)}
                    style={{ padding: '2px 7px', fontSize: 10, border: 'none', cursor: 'pointer', fontFamily: 'monospace', background: tagFormat === f ? '#30363d' : 'transparent', color: tagFormat === f ? '#e6edf3' : '#6e7681' }}>
                    {lbl}
                  </button>
                ))}
              </div>
              {/* Column tags + built-in tags */}
              {[...listCols, 'date', 'time', 'sender_name', 'sender_email'].map(col => {
                const tag = tagFormat === 'curly' ? `{{${col}}}` : tagFormat === 'bracket' ? `[${col}]` : `%${col}%`;
                return (
                  <button key={col} onClick={() => copyTag(col)} title={`Click to copy ${tag}`}
                    style={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 3, color: '#58a6ff', fontSize: 10, padding: '2px 7px', cursor: 'pointer', fontFamily: 'monospace' }}>
                    {tag}
                  </button>
                );
              })}
              {tagToast && <span style={{ fontSize: 10, color: '#3fb950', marginLeft: 4 }}>{tagToast}</span>}
              <button onClick={() => { setListItems(null); setListCols([]); setShowListPanel(false); }}
                style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#6e7681', cursor: 'pointer', fontSize: 10, padding: '0 4px' }}>✕ Clear list</button>
            </div>
          )}

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            {rawHtml ? (
              /* Raw HTML locked preview */
              <>
                <div style={{ padding: '5px 12px', background: '#161b22', borderBottom: '1px solid #21262d', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 11, color: '#d29922' }}>🔒 Raw HTML — locked for editing. Tags will be substituted at send time.</span>
                </div>
                <iframe
                  srcDoc={`<html><head><style>body{font-family:-apple-system,'Segoe UI',sans-serif;font-size:14px;line-height:1.6;padding:14px 16px;margin:0;color:#000}img{max-width:100%}*{box-sizing:border-box}</style></head><body>${rawHtml.html}</body></html>`}
                  style={{ flex: 1, border: 'none', background: '#fff', display: 'block' }}
                  sandbox="allow-same-origin"
                  title="raw-html-preview"
                />
              </>
            ) : (
              <>
                {editorMode === 'wysiwyg' && (
                  <RichEditor html={form.body} onChange={body => setForm(f => ({ ...f, body }))} />
                )}
                {editorMode === 'html' && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#0d1117', borderTop: '1px solid #30363d' }}>
                    <div style={{ padding: '4px 12px', fontSize: 10, color: '#8b949e', background: '#161b22', borderBottom: '1px solid #21262d', fontFamily: 'monospace' }}>
                      HTML Source — edits here update the rich view
                    </div>
                    <textarea
                      value={form.body}
                      onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                      spellCheck={false}
                      style={{ flex: 1, background: '#0d1117', color: '#79c0ff', border: 'none', padding: '12px 14px', fontFamily: "'Courier New', Consolas, monospace", fontSize: 12, lineHeight: 1.6, resize: 'none', outline: 'none', minHeight: 220 }}
                    />
                  </div>
                )}
                {editorMode === 'plain' && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#ffffff', borderTop: '1px solid #30363d' }}>
                    <div style={{ padding: '4px 12px', fontSize: 10, color: '#8b949e', background: '#161b22', borderBottom: '1px solid #21262d' }}>
                      Plain text — no formatting, sent as-is
                    </div>
                    <textarea
                      value={form.body}
                      onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                      placeholder="Write your message…"
                      style={{ flex: 1, background: '#ffffff', color: '#000000', border: 'none', padding: '14px 16px', fontFamily: "'Segoe UI', -apple-system, sans-serif", fontSize: 14, lineHeight: 1.6, resize: 'none', outline: 'none', minHeight: 220 }}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Quoted original email — shown below editor, read-only */}
          {form.quoted_html && (
            <div style={{ borderTop: '2px dashed #30363d', flexShrink: 0, maxHeight: 160, overflow: 'hidden', position: 'relative' }}>
              <div style={{ padding: '3px 12px', fontSize: 10, color: '#6e7681', background: '#161b22', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>— Original message —</span>
                <button onClick={() => setForm(f => ({ ...f, quoted_html: '' }))} style={{ background: 'none', border: 'none', color: '#6e7681', cursor: 'pointer', fontSize: 10 }}>Remove</button>
              </div>
              <iframe
                srcDoc={`<html><head><style>body{font-family:-apple-system,'Segoe UI',sans-serif;font-size:13px;line-height:1.5;color:#24292f;padding:8px 12px;margin:0}img{max-width:100%}*{box-sizing:border-box}</style></head><body>${form.quoted_html}</body></html>`}
                style={{ width: '100%', height: 130, border: 'none', background: '#fff', display: 'block' }}
                sandbox="allow-same-origin"
                title="quoted-email"
              />
            </div>
          )}

          {/* Send-to-list panel */}
          {showListPanel && !isReply && (
            <div style={{ borderTop: '1px solid #30363d', background: '#0d1117', padding: '10px 14px', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#e6edf3' }}>📋 Send to List</span>
                <button onClick={() => setShowListPanel(false)} style={{ background: 'none', border: 'none', color: '#6e7681', cursor: 'pointer', fontSize: 12 }}>✕</button>
              </div>
              {/* File drop zone */}
              <div onClick={() => listInputRef.current?.click()}
                style={{ border: `1px dashed ${listItems ? '#2ea043' : '#30363d'}`, borderRadius: 6, padding: '8px 12px', cursor: 'pointer', marginBottom: 8, textAlign: 'center', background: '#161b22', transition: 'border-color .15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = listItems ? '#3fb950' : '#58a6ff'}
                onMouseLeave={e => e.currentTarget.style.borderColor = listItems ? '#2ea043' : '#30363d'}>
                {listItems ? (
                  <span style={{ fontSize: 12, color: '#3fb950' }}>✓ {listItems.length} contacts — columns: {listCols.join(', ')}</span>
                ) : (
                  <span style={{ fontSize: 12, color: '#8b949e' }}>Click to upload .txt or .csv contact list</span>
                )}
              </div>
              {/* View/Edit List button */}
              {listItems?.length > 0 && (
                <button onClick={() => setShowListModal(true)}
                  style={{ width: '100%', background: 'none', border: '1px solid #30363d', borderRadius: 6, color: '#58a6ff', fontSize: 11, fontWeight: 500, padding: '5px 0', cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 }}>
                  👁 View / Edit List ({listItems.length} contacts)
                </button>
              )}
              {/* Interval */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: '#8b949e', flexShrink: 0 }}>Interval:</span>
                <input type="number" value={sendInterval} min={20} max={3600}
                  onChange={e => setSendInterval(Math.max(20, parseInt(e.target.value) || 20))}
                  style={{ width: 70, background: '#161b22', border: '1px solid #30363d', borderRadius: 4, color: '#e6edf3', fontSize: 12, padding: '4px 8px', fontFamily: 'inherit', outline: 'none' }} />
                <span style={{ fontSize: 12, color: '#8b949e' }}>seconds <span style={{ color: '#6e7681' }}>(min 20)</span></span>
              </div>
              {/* Start button */}
              <button onClick={startListCampaign} disabled={!listItems?.length}
                style={{ width: '100%', background: listItems?.length ? '#238636' : '#21262d', border: 'none', borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 600, padding: '8px 0', cursor: listItems?.length ? 'pointer' : 'not-allowed', fontFamily: 'inherit', opacity: listItems?.length ? 1 : 0.5, transition: 'background .15s' }}
                onMouseEnter={e => { if (listItems?.length) e.currentTarget.style.background = '#2ea043'; }}
                onMouseLeave={e => { if (listItems?.length) e.currentTarget.style.background = '#238636'; }}>
                {listItems?.length ? `▶ Start Campaign — ${listItems.length} emails · ${sendInterval}s interval` : 'Upload a list first'}
              </button>
              <div style={{ fontSize: 10, color: '#6e7681', marginTop: 5 }}>Sends one email at a time. Use tags in subject/body — click a tag above to copy it.</div>
            </div>
          )}

          {/* ── View/Edit List modal ── */}
          {showListModal && listItems && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 60, display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: '#161b22', borderTop: '1px solid #30363d', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #21262d', flexShrink: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#e6edf3' }}>List — {listItems.length} contacts</span>
                  <button onClick={() => setShowListModal(false)} style={{ background: 'none', border: 'none', color: '#6e7681', cursor: 'pointer', fontSize: 14 }}>✕</button>
                </div>
                <div style={{ flex: 1, overflow: 'auto', padding: '0 4px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, color: '#e6edf3' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#0d1117' }}>
                      <tr>
                        <th style={{ padding: '5px 8px', textAlign: 'left', color: '#6e7681', fontWeight: 600, borderBottom: '1px solid #21262d' }}>#</th>
                        {listCols.map(c => (
                          <th key={c} style={{ padding: '5px 8px', textAlign: 'left', color: '#6e7681', fontWeight: 600, borderBottom: '1px solid #21262d' }}>{c}</th>
                        ))}
                        <th style={{ padding: '5px 8px', borderBottom: '1px solid #21262d', width: 28 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {listItems.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #21262d' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#1c2128'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '4px 8px', color: '#6e7681' }}>{i + 1}</td>
                          {listCols.map(c => (
                            <td key={c} style={{ padding: '4px 8px' }}>
                              <input
                                defaultValue={row[c] || ''}
                                onChange={e => {
                                  const updated = [...listItems];
                                  updated[i] = { ...updated[i], [c]: e.target.value };
                                  setListItems(updated);
                                }}
                                style={{ background: 'transparent', border: 'none', color: '#e6edf3', fontSize: 11, width: '100%', outline: 'none', fontFamily: 'inherit' }}
                                onFocus={e => e.target.style.background = '#0d1117'}
                                onBlur={e => e.target.style.background = 'transparent'}
                              />
                            </td>
                          ))}
                          <td style={{ padding: '4px 4px', textAlign: 'center' }}>
                            <button onClick={() => { const u = listItems.filter((_, j) => j !== i); setListItems(u.length ? u : null); if (!u.length) setShowListModal(false); }}
                              style={{ background: 'none', border: 'none', color: '#6e7681', cursor: 'pointer', fontSize: 12, padding: '0 4px' }}
                              title="Remove row">✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ padding: '8px 12px', borderTop: '1px solid #21262d', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                  <button onClick={() => setShowListModal(false)} style={{ background: '#238636', border: 'none', borderRadius: 5, color: '#fff', fontSize: 12, fontWeight: 600, padding: '5px 16px', cursor: 'pointer', fontFamily: 'inherit' }}>Done</button>
                </div>
              </div>
            </div>
          )}

          <div style={{ padding: '7px 12px', background: '#1c2128', borderTop: '1px solid #30363d', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: '#6e7681' }}>{lastSaved ? `Saved ${fmtAgo(lastSaved)}` : 'Unsaved'}</span>
              <button onClick={() => htmlInputRef.current?.click()} title="Upload HTML template"
                style={{ background: 'none', border: '1px solid #30363d', borderRadius: 4, color: rawHtml ? '#d29922' : '#8b949e', cursor: 'pointer', fontSize: 11, padding: '3px 7px', fontFamily: 'inherit', transition: 'color .1s, border-color .1s' }}
                onMouseEnter={e => { e.currentTarget.style.color = '#e6edf3'; e.currentTarget.style.borderColor = '#8b949e'; }}
                onMouseLeave={e => { e.currentTarget.style.color = rawHtml ? '#d29922' : '#8b949e'; e.currentTarget.style.borderColor = '#30363d'; }}>
                {rawHtml ? '🔒 Raw HTML' : '↑ HTML'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => { ls.delDraft(draftId); onClose(); }} style={{ fontSize: 12 }}>Discard</button>
              <button className="btn btn-ghost btn-sm" onClick={() => { ls.saveDraft({ id: draftId, ...form }); setLastSaved(new Date().toISOString()); }} style={{ fontSize: 12 }}>Save</button>
              {!isReply && (
                <button className="btn btn-ghost btn-sm" onClick={() => setShowListPanel(v => !v)} style={{ fontSize: 12, color: listItems ? '#3fb950' : undefined }}>
                  📋{listItems ? ` ${listItems.length}` : ' List'}
                </button>
              )}
              <button className="btn btn-primary btn-sm" onClick={listItems ? startListCampaign : send} disabled={sending} style={{ minWidth: 80, fontSize: 12 }}>
                {sending ? 'Sending…' : listItems ? `▶ Campaign (${listItems.length})` : '▶ Send'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Account Settings Modal ────────────────────────────────────────────────────

const RULE_CONDITION_LABELS = { from: 'From address', subject_contains: 'Subject contains', body_contains: 'Body contains', has_attachments: 'Has attachments' };
const RULE_ACTION_LABELS    = { move_to_folder: 'Move to folder', mark_as_read: 'Mark as read', delete: 'Delete', forward_to: 'Forward to', importance: 'Set importance' };

function RulesTab({ account, folders: propFolders }) {
  const [rules, setRules]         = useState(null);
  const [rulesErr, setRulesErr]   = useState('');
  const [creating, setCreating]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saveErr, setSaveErr]     = useState('');
  const [allFolders, setAllFolders] = useState(propFolders || []);
  const [form, setForm]           = useState({
    name: '', conditions: { type: 'from', value: '' },
    actions: { type: 'move_to_folder', value: '' },
  });

  useEffect(() => {
    api.get(`/api/emails/${account.id}/rules`)
      .then(r => setRules(r.data))
      .catch(e => setRulesErr(e.response?.data?.detail || 'Failed to load rules'));
    // Fetch full folder list so we can resolve any folder ID in existing rules
    api.get(`/api/emails/${account.id}/folders`)
      .then(r => setAllFolders(r.data))
      .catch(() => {});
  }, [account.id]);

  const deleteRule = async (id) => {
    try {
      await api.delete(`/api/emails/${account.id}/rules/${encodeURIComponent(id)}`);
      setRules(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      setRulesErr(e.response?.data?.detail || 'Failed to delete rule');
    }
  };

  const saveRule = async () => {
    setSaving(true); setSaveErr('');
    try {
      const conditions = {};
      const actions = {};
      if (form.conditions.type === 'has_attachments') {
        conditions.has_attachments = true;
      } else {
        conditions[form.conditions.type] = [form.conditions.value.trim()];
      }
      if (form.actions.type === 'mark_as_read' || form.actions.type === 'delete') {
        actions[form.actions.type] = true;
      } else {
        actions[form.actions.type] = form.actions.type === 'forward_to'
          ? [form.actions.value.trim()] : form.actions.value.trim();
      }
      const { data } = await api.post(`/api/emails/${account.id}/rules`, {
        name: form.name || 'ClassMail Rule', conditions, actions,
      });
      setRules(prev => [...(prev || []), data]);
      setCreating(false);
      setForm({ name: '', conditions: { type: 'from', value: '' }, actions: { type: 'move_to_folder', value: '' } });
    } catch (e) {
      setSaveErr(e.response?.data?.detail || 'Failed to save rule');
    }
    setSaving(false);
  };

  const needsValue = (type) => !['mark_as_read', 'delete', 'has_attachments'].includes(type);
  const folderName = (id) => allFolders.find(f => f.id === id)?.name || id;

  const ruleConditionText = (cond) => {
    if (cond.from?.length)             return `From: ${cond.from.join(', ')}`;
    if (cond.subject_contains?.length) return `Subject contains: "${cond.subject_contains.join(', ')}"`;
    if (cond.body_contains?.length)    return `Body contains: "${cond.body_contains.join(', ')}"`;
    if (cond.has_attachments)          return 'Has attachments';
    return 'Any email';
  };

  const ruleActions = (act) => {
    const parts = [];
    if (act.move_to_folder)       parts.push(`Move to "${folderName(act.move_to_folder)}"`);
    if (act.mark_as_read)         parts.push('Mark as read');
    if (act.delete)               parts.push('Delete');
    if (act.forward_to?.length)   parts.push(`Forward to ${act.forward_to.join(', ')}`);
    if (act.importance)           parts.push(`Mark as ${act.importance} importance`);
    return parts;
  };

  return (
    <div>
      {rulesErr && <div style={{ color: '#f85149', fontSize: 12, marginBottom: 10 }}>{rulesErr}</div>}
      {rules === null && !rulesErr && <div style={{ color: '#8b949e', fontSize: 13 }}>Loading rules…</div>}
      {rules && rules.length === 0 && !creating && (
        <div style={{ color: '#8b949e', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No rules yet. Rules run server-side — they work even when the app is closed.</div>
      )}
      {rules && rules.map(rule => (
        <div key={rule.id} style={{ background: '#0d1117', border: '1px solid #21262d', borderRadius: 8, padding: '12px 14px', marginBottom: 8, overflow: 'hidden', minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 13, color: '#e6edf3' }}>{rule.name || 'Untitled Rule'}</span>
            <button onClick={() => deleteRule(rule.id)} style={{ background: 'none', border: 'none', color: '#f85149', cursor: 'pointer', fontSize: 12, flexShrink: 0, marginLeft: 8 }}>✕ Delete</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12 }}>
              <span style={{ background: 'rgba(88,166,255,.15)', color: '#58a6ff', borderRadius: 4, padding: '2px 7px', fontWeight: 700, fontSize: 11, flexShrink: 0 }}>IF</span>
              <span style={{ color: '#c9d1d9', wordBreak: 'break-word', minWidth: 0, flex: 1 }}>{ruleConditionText(rule.conditions)}</span>
            </div>
            {ruleActions(rule.actions).map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12 }}>
                <span style={{ background: 'rgba(35,134,54,.2)', color: '#3fb950', borderRadius: 4, padding: '2px 7px', fontWeight: 700, fontSize: 11, flexShrink: 0 }}>THEN</span>
                <span style={{ color: '#c9d1d9', wordBreak: 'break-word', minWidth: 0, flex: 1 }}>{a}</span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {creating ? (
        <div style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: 8, padding: 14, marginTop: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e6edf3', marginBottom: 10 }}>New Rule</div>
          <input placeholder="Rule name (optional)" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            style={{ width: '100%', background: '#161b22', border: '1px solid #30363d', borderRadius: 6, padding: '6px 10px', color: '#e6edf3', fontSize: 13, marginBottom: 10, fontFamily: 'inherit' }} />
          <div style={{ fontSize: 11, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Condition</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <select value={form.conditions.type} onChange={e => setForm(f => ({ ...f, conditions: { type: e.target.value, value: '' } }))}
              style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 6, padding: '6px 8px', color: '#e6edf3', fontSize: 13, fontFamily: 'inherit' }}>
              {Object.entries(RULE_CONDITION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {form.conditions.type !== 'has_attachments' && (
              <input placeholder="Value" value={form.conditions.value} onChange={e => setForm(f => ({ ...f, conditions: { ...f.conditions, value: e.target.value } }))}
                style={{ flex: 1, background: '#161b22', border: '1px solid #30363d', borderRadius: 6, padding: '6px 10px', color: '#e6edf3', fontSize: 13, fontFamily: 'inherit' }} />
            )}
          </div>
          <div style={{ fontSize: 11, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Action</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <select value={form.actions.type} onChange={e => setForm(f => ({ ...f, actions: { type: e.target.value, value: '' } }))}
              style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 6, padding: '6px 8px', color: '#e6edf3', fontSize: 13, fontFamily: 'inherit' }}>
              {Object.entries(RULE_ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {needsValue(form.actions.type) && (
              form.actions.type === 'move_to_folder' ? (
                <select value={form.actions.value} onChange={e => setForm(f => ({ ...f, actions: { ...f.actions, value: e.target.value } }))}
                  style={{ flex: 1, background: '#161b22', border: '1px solid #30363d', borderRadius: 6, padding: '6px 8px', color: '#e6edf3', fontSize: 13, fontFamily: 'inherit' }}>
                  <option value="">Select folder…</option>
                  {allFolders.map(fl => <option key={fl.id} value={fl.id}>{fl.name}</option>)}
                </select>
              ) : form.actions.type === 'importance' ? (
                <select value={form.actions.value} onChange={e => setForm(f => ({ ...f, actions: { ...f.actions, value: e.target.value } }))}
                  style={{ flex: 1, background: '#161b22', border: '1px solid #30363d', borderRadius: 6, padding: '6px 8px', color: '#e6edf3', fontSize: 13, fontFamily: 'inherit' }}>
                  <option value="">Select…</option>
                  <option value="high">High</option>
                  <option value="normal">Normal</option>
                  <option value="low">Low</option>
                </select>
              ) : (
                <input placeholder={form.actions.type === 'forward_to' ? 'email@example.com' : 'Value'}
                  value={form.actions.value} onChange={e => setForm(f => ({ ...f, actions: { ...f.actions, value: e.target.value } }))}
                  style={{ flex: 1, background: '#161b22', border: '1px solid #30363d', borderRadius: 6, padding: '6px 10px', color: '#e6edf3', fontSize: 13, fontFamily: 'inherit' }} />
              )
            )}
          </div>
          {saveErr && <div style={{ color: '#f85149', fontSize: 12, marginBottom: 8 }}>{saveErr}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setCreating(false)}>Cancel</button>
            <button className="btn btn-sm" style={{ background: '#238636', border: 'none', color: '#fff' }} onClick={saveRule} disabled={saving}>
              {saving ? 'Saving…' : 'Save Rule'}
            </button>
          </div>
        </div>
      ) : (
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 8, width: '100%' }} onClick={() => setCreating(true)}>
          + New Rule
        </button>
      )}
    </div>
  );
}

function AccountSettingsModal({ account, onClose, onDisconnect, onReconnected, folders }) {
  const [tab, setTab]           = useState('info');
  const [loading, setLoading]   = useState(false);
  const [msg, setMsg]           = useState('');
  const [showReconnect, setShowReconnect] = useState(false);

  const disconnect = async () => {
    if (!window.confirm(`Remove ${account.email}? This cannot be undone.`)) return;
    setLoading(true);
    try {
      await api.delete(`/api/emails/accounts/${account.id}`);
      onDisconnect(account.id);
      onClose();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Failed to remove account');
    }
    setLoading(false);
  };

  const tabBtn = (id, label) => (
    <button onClick={() => setTab(id)} style={{ background: 'none', border: 'none', padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', color: tab === id ? '#58a6ff' : '#8b949e', borderBottom: `2px solid ${tab === id ? '#58a6ff' : 'transparent'}`, fontWeight: tab === id ? 600 : 400 }}>
      {label}
    </button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 12, width: 480, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,.6)', display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: account.provider === 'microsoft' ? '#0078d4' : '#ea4335', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
              {account.provider === 'microsoft' ? '🪟' : '✉️'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#e6edf3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{account.display_name || account.email}</div>
              <div style={{ fontSize: 12, color: '#8b949e' }}>{account.email}</div>
            </div>
            <span style={{ fontSize: 11, background: account.status === 'active' ? 'rgba(35,134,54,.2)' : 'rgba(248,81,73,.2)', color: account.status === 'active' ? '#3fb950' : '#f85149', border: `1px solid ${account.status === 'active' ? '#238636' : '#f85149'}`, borderRadius: 20, padding: '2px 10px', fontWeight: 600, flexShrink: 0 }}>
              {account.status}
            </span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8b949e', cursor: 'pointer', fontSize: 16, padding: 4, flexShrink: 0 }}>✕</button>
          </div>
          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #21262d', marginBottom: 0 }}>
            {tabBtn('info', 'Info')}
            {tabBtn('rules', 'Rules')}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 24px', overflowY: 'auto', flex: 1 }}>
          {tab === 'info' && (
            <>
              <div style={{ background: '#0d1117', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
                {[
                  ['Provider', account.provider === 'microsoft' ? 'Microsoft 365 / Outlook' : 'Google / Gmail'],
                  ['Connected', account.connected_at ? new Date(account.connected_at).toLocaleString() : '—'],
                  ['Last sync', account.last_synced_at ? new Date(account.last_synced_at).toLocaleString() : 'Never'],
                  ['Connected via', account.connected_via === 'html_export' ? 'Exported HTML page' : 'In-app OAuth'],
                  ...(account.owner_username ? [['Owner', account.owner_username]] : []),
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #21262d' }}>
                    <span style={{ color: '#8b949e' }}>{k}</span>
                    <span style={{ color: '#e6edf3', maxWidth: 220, textAlign: 'right', wordBreak: 'break-all' }}>{v}</span>
                  </div>
                ))}
              </div>
              {account.status !== 'active' && (
                <div style={{ marginBottom: 16, padding: '12px 14px', background: 'rgba(248,81,73,.08)', border: '1px solid rgba(248,81,73,.4)', borderRadius: 8, fontSize: 13 }}>
                  <div style={{ fontWeight: 600, color: '#f85149', marginBottom: 4 }}>Session expired</div>
                  <div style={{ color: '#8b949e', fontSize: 12, marginBottom: 10 }}>Access token was revoked. Reconnect to restore access.</div>
                  <button className="btn btn-sm" style={{ background: '#0078d4', border: 'none', color: '#fff', fontWeight: 600 }} onClick={() => setShowReconnect(true)}>
                    🔄 Reconnect Account
                  </button>
                </div>
              )}
              {msg && <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(248,81,73,.1)', border: '1px solid #f85149', borderRadius: 6, color: '#f85149', fontSize: 12 }}>{msg}</div>}
            </>
          )}
          {tab === 'rules' && <RulesTab account={account} folders={folders} />}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid #21262d', display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          <button className="btn btn-danger btn-sm" onClick={disconnect} disabled={loading}>
            {loading ? 'Removing…' : '🗑 Remove Account'}
          </button>
        </div>
      </div>
      {showReconnect && (
        <DeviceCodeModal
          accountToReconnect={account}
          onClose={() => setShowReconnect(false)}
          onSuccess={(s) => { setShowReconnect(false); onReconnected?.(s); onClose(); }}
        />
      )}
    </div>
  );
}

// ── Main Email Client ─────────────────────────────────────────────────────────

export default function EmailClient() {
  const { accountId: urlAccountId } = useParams();
  const navigate = useNavigate();
  const { user } = useStore();

  const [accounts, setAccounts] = useState([]);
  const [acctFolders, setAcctFolders] = useState({});
  const [expanded, setExpanded] = useState(new Set());

  const [activeAcct, setActiveAcct] = useState(urlAccountId ? parseInt(urlAccountId) : null);
  const [activeFolder, setActiveFolder] = useState('inbox');

  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [msgBody, setMsgBody] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingBody, setLoadingBody] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [nextPage, setNextPage] = useState(null);

  const [compose, setCompose] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [actionMsg, setActionMsg] = useState('');
  const actionTimer = useRef(null);

  const [localDraftsCount, setLocalDraftsCount] = useState(() => ls.getDrafts().length);
  const [localSentCount, setLocalSentCount] = useState(() => ls.getSent().length);

  const [settingsAccount, setSettingsAccount] = useState(null);

  const refreshLocalCounts = () => {
    setLocalDraftsCount(ls.getDrafts().length);
    setLocalSentCount(ls.getSent().length);
  };

  const toast = (msg) => {
    setActionMsg(msg);
    clearTimeout(actionTimer.current);
    actionTimer.current = setTimeout(() => setActionMsg(''), 3000);
  };

  // ── Folder loading ─────────────────────────────────────────────────────────

  const loadFolders = useCallback(async (acctId) => {
    setAcctFolders(prev => ({ ...prev, [acctId]: { ...(prev[acctId] || {}), loading: true } }));
    try {
      const { data } = await api.get(`/api/emails/${acctId}/folders`);
      setAcctFolders(prev => ({ ...prev, [acctId]: { loading: false, loaded: true, folders: data, error: false } }));
    } catch {
      setAcctFolders(prev => ({
        ...prev,
        [acctId]: {
          loading: false, loaded: true, error: true,
          folders: [
            { id: 'inbox', name: 'Inbox', unread_count: 0, total_count: 0 },
            { id: 'sent', name: 'Sent', unread_count: 0, total_count: 0 },
            { id: 'drafts', name: 'Drafts', unread_count: 0, total_count: 0 },
            { id: 'spam', name: 'Spam', unread_count: 0, total_count: 0 },
            { id: 'trash', name: 'Trash', unread_count: 0, total_count: 0 },
          ]
        }
      }));
    }
  }, []);

  const toggleExpand = useCallback((acctId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(acctId)) {
        next.delete(acctId);
      } else {
        next.add(acctId);
        if (!acctFolders[acctId]?.loaded) loadFolders(acctId);
      }
      return next;
    });
  }, [acctFolders, loadFolders]);

  // ── Load accounts on mount ─────────────────────────────────────────────────

  useEffect(() => {
    api.get('/api/emails/accounts').then(r => {
      const list = r.data.filter(a => a.status !== 'revoked');
      setAccounts(list);
      if (!list.length) return;

      const initId = urlAccountId ? parseInt(urlAccountId) : list.find(a => a.status === 'active')?.id;
      if (!initId) return;

      setActiveAcct(initId);
      setActiveFolder('inbox');
      setExpanded(new Set([initId]));
      loadFolders(initId);
    }).catch(() => {});
  }, []);

  // ── Load messages ──────────────────────────────────────────────────────────

  const loadMessages = useCallback(async (acctId, folder, pageToken = null) => {
    if (folder === 'local:drafts' || folder === 'local:sent' || !acctId) return;
    setLoading(true);
    setLoadError('');
    if (!pageToken) { setMessages([]); setSelected(null); setMsgBody(null); }
    try {
      const params = new URLSearchParams({ folder });
      if (pageToken) params.set('page_token', pageToken);
      const { data } = await api.get(`/api/emails/${acctId}/messages?${params}`);
      setMessages(prev => pageToken ? [...prev, ...data.messages] : data.messages);
      setNextPage(data.next_page_token || null);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Failed to load messages';
      setLoadError(detail);
    }
    setLoading(false);
  }, []);

  const selectFolder = useCallback((acctId, folderId) => {
    setActiveAcct(acctId);
    setActiveFolder(folderId);
    setMessages([]);
    setSelected(null);
    setMsgBody(null);
    setSearchResults(null);
    setSearchQ('');
    setLoadError('');
    if (acctId && folderId !== 'local:drafts' && folderId !== 'local:sent') {
      loadMessages(acctId, folderId);
      if (acctId !== (urlAccountId ? parseInt(urlAccountId) : null)) {
        navigate(`/email/${acctId}`, { replace: true });
      }
    }
  }, [loadMessages, navigate, urlAccountId]);

  // Initial message load after accounts set
  useEffect(() => {
    if (activeAcct && activeFolder && activeFolder !== 'local:drafts' && activeFolder !== 'local:sent') {
      loadMessages(activeAcct, activeFolder);
    }
  }, [activeAcct]);

  // ── Message operations ─────────────────────────────────────────────────────

  const openMessage = async (msg) => {
    if (msg._isDraft) { setCompose({ ...msg._draftData, draftId: msg._draftData.id }); return; }
    if (msg._isSent) {
      if (selected === msg.id) return;
      setSelected(msg.id);
      setMsgBody({ _isLocalSent: true, _sentData: msg._sentData, subject: msg._sentData.subject, from: msg._sentData.from_email || '', from_name: 'Me', to: msg._sentData.to ? [msg._sentData.to] : [], date: msg._sentData.sent_at, body_html: msg._sentData.body, body_type: 'html', is_read: true, is_starred: false });
      return;
    }
    if (selected === msg.id) return;
    const owningAcctId = msg.account_id ?? activeAcct;
    setSelected(msg.id);
    setMsgBody(null);
    setLoadingBody(true);
    try {
      const { data } = await api.get(`/api/emails/${owningAcctId}/messages/${msg.id}`);
      setMsgBody({ ...data, _accountId: owningAcctId, _msgId: msg.id, has_attachments: msg.has_attachments ?? data.has_attachments });
      if (!msg.is_read) {
        await api.patch(`/api/emails/${owningAcctId}/messages/${msg.id}/read`, { is_read: true }).catch(() => {});
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
      }
    } catch {}
    setLoadingBody(false);
  };

  const deleteMsg = async () => {
    if (!selected || !activeAcct) return;
    await api.delete(`/api/emails/${activeAcct}/messages/${selected}`).catch(() => {});
    setMessages(prev => prev.filter(m => m.id !== selected));
    setSelected(null); setMsgBody(null); toast('Message deleted');
    loadFolders(activeAcct);
  };

  const spamMsg = async (spam = true) => {
    if (!selected || !activeAcct) return;
    await api.post(`/api/emails/${activeAcct}/messages/${selected}/spam`, { spam }).catch(() => {});
    setMessages(prev => prev.filter(m => m.id !== selected));
    setSelected(null); setMsgBody(null);
    toast(spam ? 'Marked as spam' : 'Removed from spam');
    loadFolders(activeAcct);
  };

  const moveMsg = async (dest) => {
    if (!selected || !activeAcct) return;
    await api.post(`/api/emails/${activeAcct}/messages/${selected}/move`, { destination: dest }).catch(() => {});
    setMessages(prev => prev.filter(m => m.id !== selected));
    setSelected(null); setMsgBody(null); toast(`Moved to ${dest}`);
    loadFolders(activeAcct);
  };

  const markRead = async (is_read) => {
    if (!selected || !activeAcct) return;
    await api.patch(`/api/emails/${activeAcct}/messages/${selected}/read`, { is_read }).catch(() => {});
    setMessages(prev => prev.map(m => m.id === selected ? { ...m, is_read } : m));
    setMsgBody(prev => prev ? { ...prev, is_read } : prev);
    toast(is_read ? 'Marked as read' : 'Marked as unread');
  };

  const toggleStar = async () => {
    if (!selected || !msgBody) return;
    const starred = !msgBody.is_starred;
    await api.post(`/api/emails/${msgBody._accountId ?? activeAcct}/messages/${selected}/star`, { starred }).catch(() => {});
    setMsgBody(prev => prev ? { ...prev, is_starred: starred } : prev);
    setMessages(prev => prev.map(m => m.id === selected ? { ...m, is_starred: starred } : m));
    toast(starred ? 'Starred' : 'Star removed');
  };

  const printMsg = () => {
    if (!msgBody) return;
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${msgBody.subject || 'Email'}</title><style>body{font-family:'Segoe UI',Arial,sans-serif;font-size:14px;line-height:1.6;color:#111;padding:24px;max-width:800px;margin:0 auto}.hdr{border-bottom:2px solid #eee;padding-bottom:16px;margin-bottom:20px}.meta{font-size:12px;color:#555;margin-top:4px}</style></head><body><div class="hdr"><h2 style="margin:0 0 8px">${msgBody.subject || '(no subject)'}</h2><div class="meta"><b>From:</b> ${msgBody.from_name ? `${msgBody.from_name} &lt;${msgBody.from}&gt;` : msgBody.from}</div><div class="meta"><b>To:</b> ${(msgBody.to || []).join(', ')}</div><div class="meta"><b>Date:</b> ${fmtFull(msgBody.date)}</div></div>${msgBody.body_html || ''}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  // ── Search ─────────────────────────────────────────────────────────────────

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!searchQ.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/api/emails/search?q=${encodeURIComponent(searchQ)}&max_results=40`);
      setSearchResults(data);
    } catch {}
    setSearching(false);
  };

  const clearSearch = () => { setSearchQ(''); setSearchResults(null); };

  // ── Display messages ───────────────────────────────────────────────────────

  const localDraftMessages = activeFolder === 'local:drafts'
    ? ls.getDrafts().map(d => ({ id: d.id, from: d.to || '', from_name: 'Draft', subject: d.subject || '(no subject)', preview: d.body?.replace(/<[^>]+>/g, '').slice(0, 80) || '', date: d.saved_at, is_read: true, _isDraft: true, _draftData: d }))
    : null;

  const localSentMessages = activeFolder === 'local:sent'
    ? ls.getSent().map(s => ({ id: s.id, from: s.from_email || '', from_name: 'Sent', subject: s.subject || '(no subject)', preview: s.body?.replace(/<[^>]+>/g, '').slice(0, 80) || '', date: s.sent_at, is_read: true, _isSent: true, _sentData: s }))
    : null;

  const displayMessages = searchResults !== null
    ? searchResults
    : activeFolder === 'local:drafts' ? (localDraftMessages || [])
    : activeFolder === 'local:sent' ? (localSentMessages || [])
    : messages;

  const activeFolderLabel = activeFolder === 'local:drafts' ? 'Local Drafts'
    : activeFolder === 'local:sent' ? 'Local Sent'
    : (() => {
      const folders = acctFolders[activeAcct]?.folders || [];
      return folders.find(f => f.id === activeFolder)?.name || activeFolder;
    })();

  const activeAccount = accounts.find(a => a.id === activeAcct);

  // ── Folder button style ────────────────────────────────────────────────────

  const folderBtn = (acctId, folderId) => ({
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    padding: '7px 12px 7px 32px', background: 'none', border: 'none', cursor: 'pointer',
    color: (activeAcct === acctId && activeFolder === folderId) ? 'var(--accent)' : 'var(--text2)',
    backgroundColor: (activeAcct === acctId && activeFolder === folderId) ? 'rgba(88,166,255,.1)' : 'transparent',
    borderLeft: `3px solid ${(activeAcct === acctId && activeFolder === folderId) ? 'var(--accent)' : 'transparent'}`,
    fontSize: 13, textAlign: 'left', fontFamily: 'inherit', transition: 'background .1s',
  });

  const localFolderBtn = (folderId) => ({
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    padding: '7px 12px 7px 32px', background: 'none', border: 'none', cursor: 'pointer',
    color: (activeAcct === null && activeFolder === folderId) ? 'var(--accent)' : 'var(--text2)',
    backgroundColor: (activeAcct === null && activeFolder === folderId) ? 'rgba(88,166,255,.1)' : 'transparent',
    borderLeft: `3px solid ${(activeAcct === null && activeFolder === folderId) ? 'var(--accent)' : 'transparent'}`,
    fontSize: 13, textAlign: 'left', fontFamily: 'inherit', transition: 'background .1s',
  });

  // ── Superadmin grouping + account row renderer ───────────────────────────

  const isSA = user?.role === 'superadmin';
  const accountGroups = isSA
    ? Object.entries(
        accounts.reduce((m, a) => {
          const k = a.owner_username || 'Unknown';
          if (!m[k]) m[k] = [];
          m[k].push(a);
          return m;
        }, {})
      )
    : null;

  const renderAccount = (acct) => {
    const isExpanded = expanded.has(acct.id);
    const fState = acctFolders[acct.id] || {};
    const folders = fState.folders || [];
    const isActive = acct.id === activeAcct;
    const totalUnread = folders.reduce((n, f) => n + (f.unread_count || 0), 0);
    return (
      <div key={acct.id} style={{ borderBottom: '1px solid var(--border)' }}>
        <div
          onClick={() => { toggleExpand(acct.id); if (!isExpanded && acct.status === 'active') selectFolder(acct.id, 'inbox'); }}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', cursor: 'pointer', background: isActive && !['local:drafts','local:sent'].includes(activeFolder) ? 'rgba(88,166,255,.06)' : 'transparent', transition: 'background .1s', userSelect: 'none' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.04)'}
          onMouseLeave={e => e.currentTarget.style.background = isActive && !['local:drafts','local:sent'].includes(activeFolder) ? 'rgba(88,166,255,.06)' : 'transparent'}
        >
          <span style={{ fontSize: 16, flexShrink: 0 }}>{acct.provider === 'microsoft' ? '🪟' : '✉️'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: isActive ? 'var(--text)' : 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {acct.email}
            </div>
            {isSA && acct.owner_username && (
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>{acct.owner_username}</div>
            )}
          </div>
          {totalUnread > 0 && !isExpanded && (
            <span style={{ fontSize: 10, background: 'var(--accent)', color: '#0d1117', borderRadius: 10, padding: '1px 5px', fontWeight: 700, flexShrink: 0 }}>{totalUnread}</span>
          )}
          <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: acct.status === 'active' ? 'var(--success)' : acct.status === 'expired' ? 'var(--danger)' : '#6e7681' }} title={acct.status} />
          <button title="Account settings" onClick={e => { e.stopPropagation(); setSettingsAccount(acct); }}
            style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 13, padding: '2px 4px', borderRadius: 4, flexShrink: 0, lineHeight: 1 }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}>⚙</button>
          <span style={{ fontSize: 10, color: 'var(--text3)', flexShrink: 0, transition: 'transform .15s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block' }}>▼</span>
        </div>
        {isExpanded && (
          <div>
            {fState.loading && (
              <div style={{ padding: '8px 32px', fontSize: 12, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="spinner" style={{ width: 10, height: 10 }} /> Loading folders…
              </div>
            )}
            {fState.error && (
              <div style={{ padding: '4px 32px 8px', fontSize: 11, color: 'var(--warning)' }}>
                ⚠ Token may be expired
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '1px 6px', marginLeft: 6 }} onClick={() => loadFolders(acct.id)}>Retry</button>
              </div>
            )}
            {folders.map(f => (
              <button key={f.id} onClick={() => selectFolder(acct.id, f.id)} style={folderBtn(acct.id, f.id)}
                onMouseEnter={e => { if (!(activeAcct === acct.id && activeFolder === f.id)) e.currentTarget.style.background = 'rgba(255,255,255,.04)'; }}
                onMouseLeave={e => { if (!(activeAcct === acct.id && activeFolder === f.id)) e.currentTarget.style.background = 'transparent'; }}>
                <span style={{ fontSize: 13 }}>{FOLDER_ICONS[f.id] || '📁'}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                {(f.unread_count || 0) > 0 && (
                  <span style={{ fontSize: 10, background: 'var(--accent)', color: '#0d1117', borderRadius: 10, padding: '1px 5px', fontWeight: 700, minWidth: 16, textAlign: 'center', flexShrink: 0 }}>
                    {f.unread_count}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── Layout constants ───────────────────────────────────────────────────────

  const CSS = {
    root: { display: 'flex', height: '100%', minHeight: '100vh', overflow: 'hidden', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'Segoe UI',-apple-system,BlinkMacSystemFont,sans-serif" },
    left: { width: 260, display: 'flex', flexDirection: 'column', background: 'var(--surface)', borderRight: '1px solid var(--border)', flexShrink: 0, overflow: 'hidden' },
    mid: { width: 340, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border)', background: 'var(--bg)', flexShrink: 0 },
    right: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={CSS.root}>

      {/* ══ LEFT: Account + Folder Sidebar ════════════════════════════════════ */}
      <aside style={CSS.left}>
        {/* Header */}
        <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <button className="btn btn-ghost btn-sm" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => navigate(user?.role === 'superadmin' ? '/superadmin' : '/dashboard')}>← Back</button>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)', flex: 1 }}>Mail</span>
          <button
            onClick={() => navigate(user?.role === 'superadmin' ? '/superadmin/email/extractor' : '/email/extractor')}
            style={{ background: 'rgba(31,111,235,.12)', border: '1px solid rgba(31,111,235,.3)', borderRadius: 5, color: '#1f6feb', fontSize: 11, fontWeight: 600, padding: '3px 8px', cursor: 'pointer', flexShrink: 0 }}>
            ⛏ Extractor
          </button>
        </div>

        {/* Compose */}
        <div style={{ padding: '10px 12px 8px', flexShrink: 0 }}>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: 13, padding: '8px' }}
            onClick={() => setCompose({})}>
            ✏ Compose
          </button>
        </div>

        {/* Account + Folder Tree — scrollable */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {accounts.length === 0 && (
            <div style={{ padding: '20px 16px', color: 'var(--text3)', fontSize: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>📭</div>
              No connected accounts
            </div>
          )}

          {/* Superadmin: grouped by owner. Admin: flat list. */}
          {isSA && accountGroups ? (
            accountGroups.map(([ownerName, ownerAccts]) => (
              <div key={ownerName}>
                <div style={{ padding: '6px 14px 4px', fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.5px', background: 'rgba(255,255,255,.02)', borderBottom: '1px solid var(--border)', borderTop: '1px solid var(--border)' }}>
                  👤 {ownerName}
                </div>
                {ownerAccts.map(acct => renderAccount(acct))}
              </div>
            ))
          ) : (
            accounts.map(acct => renderAccount(acct))
          )}
        </div>

        {/* Local section — pinned at bottom, never scrolls */}
        <div style={{ flexShrink: 0, borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
          <div style={{ padding: '5px 16px 2px', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 600 }}>
            Local
          </div>
          <button
            style={localFolderBtn('local:drafts')}
            onClick={() => { setActiveAcct(null); setActiveFolder('local:drafts'); setSelected(null); setMsgBody(null); setSearchResults(null); setSearchQ(''); }}
            onMouseEnter={e => { if (!(activeAcct === null && activeFolder === 'local:drafts')) e.currentTarget.style.background = 'rgba(255,255,255,.04)'; }}
            onMouseLeave={e => { if (!(activeAcct === null && activeFolder === 'local:drafts')) e.currentTarget.style.background = 'transparent'; }}>
            <span style={{ fontSize: 13 }}>✏️</span>
            <span style={{ flex: 1 }}>Local Drafts</span>
            {localDraftsCount > 0 && <span style={{ fontSize: 10, background: 'var(--accent)', color: '#0d1117', borderRadius: 10, padding: '1px 5px', fontWeight: 700 }}>{localDraftsCount}</span>}
          </button>
          <button
            style={localFolderBtn('local:sent')}
            onClick={() => { setActiveAcct(null); setActiveFolder('local:sent'); setSelected(null); setMsgBody(null); setSearchResults(null); setSearchQ(''); }}
            onMouseEnter={e => { if (!(activeAcct === null && activeFolder === 'local:sent')) e.currentTarget.style.background = 'rgba(255,255,255,.04)'; }}
            onMouseLeave={e => { if (!(activeAcct === null && activeFolder === 'local:sent')) e.currentTarget.style.background = 'transparent'; }}>
            <span style={{ fontSize: 13 }}>📤</span>
            <span style={{ flex: 1 }}>Local Sent</span>
            {localSentCount > 0 && <span style={{ fontSize: 10, background: 'var(--accent)', color: '#0d1117', borderRadius: 10, padding: '1px 5px', fontWeight: 700 }}>{localSentCount}</span>}
          </button>
        </div>
      </aside>

      {/* ══ MIDDLE: Message List ═══════════════════════════════════════════════ */}
      <div style={CSS.mid}>
        {/* Search */}
        <form onSubmit={doSearch} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 6, flexShrink: 0 }}>
          <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search all accounts…"
            style={{ flex: 1, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '7px 10px', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
          {searchResults !== null
            ? <button type="button" className="btn btn-ghost btn-sm" onClick={clearSearch}>×</button>
            : <button type="submit" className="btn btn-ghost btn-sm" disabled={searching}>{searching ? '…' : '🔍'}</button>}
        </form>

        {/* Folder heading */}
        <div style={{ padding: '8px 14px', fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span>
            {searchResults !== null
              ? `Search: "${searchQ}"`
              : activeAccount ? `${activeAccount.email.split('@')[1]} · ${activeFolderLabel}` : activeFolderLabel}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {searchResults !== null && <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text3)' }}>{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</span>}
            {loading
              ? <span className="spinner" style={{ width: 12, height: 12 }} />
              : activeAcct && activeFolder && !['local:drafts', 'local:sent'].includes(activeFolder) && (
                  <button
                    title="Refresh"
                    onClick={() => loadMessages(activeAcct, activeFolder)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 13, padding: '2px 4px', borderRadius: 4, lineHeight: 1 }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                  >⟳</button>
                )
            }
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loadError && !loading && (() => {
            const isTokenErr = loadError.includes('Token refresh failed') || loadError.includes('session expired');
            const activeAcctObj = accounts.find(a => a.id === activeAcct);
            return (
              <div style={{ margin: '16px 14px', padding: '12px 14px', background: 'rgba(248,81,73,.1)', border: '1px solid #f85149', borderRadius: 8, fontSize: 13, color: '#f85149' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {isTokenErr ? 'Account session expired' : 'Failed to load messages'}
                </div>
                <div style={{ color: '#8b949e', fontSize: 12, marginBottom: 10 }}>
                  {isTokenErr
                    ? 'The access token for this account was revoked by Microsoft. Reconnect the account to restore access.'
                    : loadError}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {isTokenErr && activeAcctObj && (
                    <button className="btn btn-sm" style={{ background: '#0078d4', border: 'none', color: '#fff', fontWeight: 600, fontSize: 12 }}
                      onClick={() => setSettingsAccount(activeAcctObj)}>
                      🔄 Reconnect Account
                    </button>
                  )}
                  {!isTokenErr && (
                    <button className="btn btn-ghost btn-sm" style={{ fontSize: 12, color: '#f85149', borderColor: '#f85149' }}
                      onClick={() => loadMessages(activeAcct, activeFolder)}>
                      Retry
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
          {!loading && !loadError && displayMessages.length === 0 && (
            <div className="empty-state" style={{ paddingTop: 50 }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
              <p style={{ fontSize: 13 }}>{searchResults !== null ? 'No results' : 'No messages'}</p>
            </div>
          )}
          {displayMessages.map(m => (
            <div key={`${m.account_id ?? activeAcct}-${m.id}`} onClick={() => openMessage(m)}
              style={{
                padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
                background: selected === m.id ? 'rgba(88,166,255,.08)' : 'transparent',
                borderLeft: `3px solid ${selected === m.id ? 'var(--accent)' : 'transparent'}`,
                transition: 'background .1s',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                {!m.is_read && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />}
                {m.is_starred && <span style={{ fontSize: 10 }}>⭐</span>}
                <span style={{ fontWeight: m.is_read ? 400 : 700, fontSize: 13, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.from_name || m.from_email || m.from}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0 }}>{fmtDate(m.date)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {m.has_attachments && <span style={{ fontSize: 11, flexShrink: 0, color: 'var(--text3)' }}>📎</span>}
                <span style={{ fontSize: 13, fontWeight: m.is_read ? 400 : 600, color: m.is_read ? 'var(--text2)' : 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.subject || '(no subject)'}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                {m.preview}
              </div>
              {searchResults !== null && m.account_email && (
                <div style={{ marginTop: 4 }}>
                  <span style={{ fontSize: 10, background: '#21262d', border: '1px solid #30363d', borderRadius: 10, padding: '1px 7px', color: '#8b949e', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <span>{m.provider === 'microsoft' ? '🪟' : '✉️'}</span>
                    <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.account_email}</span>
                  </span>
                </div>
              )}
            </div>
          ))}
          {nextPage && searchResults === null && activeFolder !== 'local:drafts' && activeFolder !== 'local:sent' && (
            <div style={{ padding: 12, textAlign: 'center' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => loadMessages(activeAcct, activeFolder, nextPage)} disabled={loading}>
                {loading ? 'Loading…' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ══ RIGHT: Reading Pane ════════════════════════════════════════════════ */}
      <div style={CSS.right}>
        {/* Toolbar */}
        {msgBody && !msgBody._isLocalSent && (
          <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', background: 'var(--surface)', flexShrink: 0 }}>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }} onClick={() => setCompose({
              from_account_id: msgBody._accountId ?? activeAcct,
              to: msgBody.from, subject: msgBody.subject?.startsWith('Re:') ? msgBody.subject : `Re: ${msgBody.subject}`,
              body: '<br>',
              quoted_html: `<div style="font-size:12px;color:#555;margin-bottom:6px"><b>From:</b> ${msgBody.from_name || ''} &lt;${msgBody.from}&gt;<br><b>Date:</b> ${fmtFull(msgBody.date)}<br><b>Subject:</b> ${msgBody.subject}</div>${msgBody.body_html || ''}`,
              message_id: selected, reply_all: false, thread_id: msgBody.thread_id,
            })}>↩ Reply</button>

            <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }} onClick={() => setCompose({
              from_account_id: msgBody._accountId ?? activeAcct,
              to: msgBody.from, cc: (msgBody.to || []).filter(Boolean).join(', '),
              subject: msgBody.subject?.startsWith('Re:') ? msgBody.subject : `Re: ${msgBody.subject}`,
              body: '<br>',
              quoted_html: `<div style="font-size:12px;color:#555;margin-bottom:6px"><b>From:</b> ${msgBody.from_name || ''} &lt;${msgBody.from}&gt;<br><b>Date:</b> ${fmtFull(msgBody.date)}<br><b>Subject:</b> ${msgBody.subject}</div>${msgBody.body_html || ''}`,
              message_id: selected, reply_all: true, thread_id: msgBody.thread_id,
            })}>↩↩ Reply All</button>

            <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }} onClick={() => setCompose({
              from_account_id: msgBody._accountId ?? activeAcct,
              subject: `Fwd: ${msgBody.subject}`, is_forward: true,
              body: '<br>',
              quoted_html: `<div style="font-size:12px;color:#555;margin-bottom:6px"><b>---------- Forwarded message ----------</b><br><b>From:</b> ${msgBody.from_name || ''} &lt;${msgBody.from}&gt;<br><b>Date:</b> ${fmtFull(msgBody.date)}<br><b>Subject:</b> ${msgBody.subject}<br><b>To:</b> ${(msgBody.to || []).join(', ')}</div>${msgBody.body_html || ''}`,
            })}>↪ Forward</button>

            <div style={{ height: 16, width: 1, background: 'var(--border)', margin: '0 2px' }} />

            <button className="btn btn-ghost btn-sm" style={{ fontSize: 12, color: msgBody.is_starred ? '#f0c040' : undefined }} onClick={toggleStar}>
              {msgBody.is_starred ? '⭐' : '☆'} {msgBody.is_starred ? 'Starred' : 'Star'}
            </button>

            <select onChange={e => { if (e.target.value) { moveMsg(e.target.value); e.target.value = ''; } }}
              style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px', color: 'var(--text2)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
              <option value="">📁 Move…</option>
              {(acctFolders[activeAcct]?.folders || []).map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>

            <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }} onClick={() => markRead(!msgBody.is_read)}>
              {msgBody.is_read ? '◎ Unread' : '● Read'}
            </button>

            {activeFolder !== 'spam'
              ? <button className="btn btn-ghost btn-sm" style={{ fontSize: 12, color: 'var(--warning)' }} onClick={() => spamMsg(true)}>🚫 Spam</button>
              : <button className="btn btn-ghost btn-sm" style={{ fontSize: 12, color: 'var(--success)' }} onClick={() => spamMsg(false)}>✓ Not Spam</button>}

            <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }} onClick={printMsg}>🖨 Print</button>

            <button className="btn btn-danger btn-sm" style={{ marginLeft: 'auto', fontSize: 12 }} onClick={deleteMsg}>🗑 Delete</button>
          </div>
        )}

        {/* Reading area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: msgBody ? '24px 32px' : 0 }}>
          {!selected && !loadingBody && (
            <div className="empty-state" style={{ marginTop: 80 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✉️</div>
              <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                {accounts.length === 0 ? 'No accounts connected' : 'Select a message'}
              </p>
              <p style={{ fontSize: 13, color: 'var(--text3)', marginTop: 6 }}>
                {accounts.length === 0 ? 'Connect an email account from the dashboard' : `${accounts.length} account${accounts.length !== 1 ? 's' : ''} connected`}
              </p>
            </div>
          )}

          {loadingBody && <div className="empty-state" style={{ paddingTop: 60 }}><span className="spinner" style={{ width: 24, height: 24 }} /></div>}

          {/* Local sent reading */}
          {msgBody && msgBody._isLocalSent && (
            <>
              <div style={{ marginBottom: 12, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', background: '#21262d', borderRadius: 20, fontSize: 11, color: '#8b949e' }}>
                📤 Locally Sent
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#e6edf3', marginBottom: 16, lineHeight: 1.4 }}>{msgBody.subject || '(no subject)'}</h2>
              <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)', fontSize: 13, color: 'var(--text2)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div><span style={{ color: 'var(--text3)' }}>To:</span> {msgBody._sentData?.to || ''}</div>
                {msgBody._sentData?.cc && <div><span style={{ color: 'var(--text3)' }}>CC:</span> {msgBody._sentData.cc}</div>}
                <div><span style={{ color: 'var(--text3)' }}>Sent:</span> {fmtFull(msgBody.date)}</div>
              </div>
              <iframe srcDoc={`<html><head><style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:14px;line-height:1.6;color:#24292f;padding:0;margin:0}img{max-width:100%;height:auto}</style></head><body>${msgBody._sentData?.body || ''}</body></html>`}
                style={{ width: '100%', minHeight: 400, border: 'none', background: '#fff', borderRadius: 8 }} sandbox="allow-same-origin" title="sent-email-body" />
            </>
          )}

          {/* Regular message */}
          {msgBody && !loadingBody && !msgBody._isLocalSent && (
            <>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#e6edf3', marginBottom: 16, lineHeight: 1.4 }}>{msgBody.subject || '(no subject)'}</h2>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, color: '#0d1117', flexShrink: 0 }}>
                  {(msgBody.from_name || msgBody.from || '?')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#e6edf3' }}>{msgBody.from_name || msgBody.from}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 1 }}>{msgBody.from}</div>
                  {msgBody.to?.length > 0 && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>To: {msgBody.to.filter(Boolean).join(', ')}</div>}
                  {msgBody.cc?.length > 0 && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>CC: {msgBody.cc.filter(Boolean).join(', ')}</div>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', flexShrink: 0 }}>{fmtFull(msgBody.date)}</div>
              </div>

              {msgBody.has_attachments && (
                <AttachmentBar accountId={msgBody._accountId ?? activeAcct} messageId={msgBody._msgId ?? selected} />
              )}

              <div style={{ borderRadius: 8, overflow: 'hidden', marginTop: 12 }}>
                {msgBody.body_type === 'html' ? (
                  <iframe
                    srcDoc={`<html><head><style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:14px;line-height:1.6;color:#24292f;padding:0;margin:0}img{max-width:100%;height:auto}a{color:#0078d4}</style></head><body>${msgBody.body_html}</body></html>`}
                    style={{ width: '100%', minHeight: 400, border: 'none', background: '#fff', borderRadius: 8 }}
                    sandbox="allow-same-origin" title="email-body"
                  />
                ) : (
                  <pre style={{ fontFamily: 'inherit', fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', color: 'var(--text)', background: 'var(--surface)', padding: 16, borderRadius: 8 }}>
                    {msgBody.body_html}
                  </pre>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Compose Window */}
      {compose !== null && (
        <ComposeWindow
          key={compose._key || compose.message_id || compose.draftId || 'new'}
          accounts={accounts.filter(a => a.status === 'active')}
          currentAccountId={activeAcct}
          initialData={compose}
          draftId={compose.draftId}
          onClose={() => { setCompose(null); refreshLocalCounts(); }}
          onSent={() => { if (activeAcct && activeFolder) loadMessages(activeAcct, activeFolder); if (activeAcct) loadFolders(activeAcct); refreshLocalCounts(); }}
        />
      )}

      {/* Account Settings Modal */}
      {settingsAccount && (
        <AccountSettingsModal
          account={settingsAccount}
          folders={acctFolders[settingsAccount.id]?.folders || []}
          onClose={() => setSettingsAccount(null)}
          onDisconnect={(id) => {
            setAccounts(prev => prev.filter(a => a.id !== id));
            setAcctFolders(prev => { const n = { ...prev }; delete n[id]; return n; });
            setExpanded(prev => { const n = new Set(prev); n.delete(id); return n; });
            if (activeAcct === id) { setActiveAcct(null); setMessages([]); setSelected(null); setMsgBody(null); }
          }}
          onReconnected={() => {
            // Refresh account list and reload messages after reconnect
            api.get('/api/emails/accounts').then(r => setAccounts((r.data || []).filter(a => a.status === 'active'))).catch(() => {});
            setSettingsAccount(null);
            if (activeAcct) { loadFolders(activeAcct); loadMessages(activeAcct, activeFolder); }
          }}
        />
      )}

      {/* Toast */}
      {actionMsg && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#238636', color: '#fff', padding: '10px 20px', borderRadius: 20, boxShadow: '0 4px 16px rgba(0,0,0,.4)', fontSize: 13, fontWeight: 600, pointerEvents: 'none' }}>
          {actionMsg}
        </div>
      )}
    </div>
  );
}
